import { useEffect, useMemo, useRef, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import {
    Alert,
    Box,
    Button,
    Checkbox,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    FormControlLabel,
    FormGroup,
    FormHelperText,
    FormLabel,
    TextField,
    Typography,
} from '@mui/material'
import { useSnackbar } from 'notistack'
import PropTypes from 'prop-types'

import { useGetQuoteAvailableDocumentsQuery, useSendInternalQuoteEmailMutation } from '@/app/services/quotes'
import { selectOrganisation } from '@/app/slices/organisationSlice'
import { RichContentEditor } from '@/common/components'
import { useToolBoxTreatments } from '@/common/hooks'
import { useFeatureFlags } from '@/common/hooks/useFeatureFlags'
import {
    getFormattedPrice,
    Paths,
    PRIVATE_DOCUMENTS,
    PUBLIC_DOCUMENTS,
    QUOTE_PAYMENT_STATUS,
    splitEmailAddresses,
    validateEmail,
} from '@/common/utils'

import 'react-draft-wysiwyg/dist/react-draft-wysiwyg.css'

// Full sentences per document type, not an interpolated noun (e.g.
// `t("Here's {{documentName}}")`) — de/fr/es/it/pt inflect the article with the
// noun's gender ("Voici le devis" vs "Voici la facture"), so the translated
// value has to be the whole sentence, not assembled from a shared template.
const DOCUMENT_EMAIL_COPY = {
    [PUBLIC_DOCUMENTS.Quote]: {
        heading: "Here's quote",
        subject: 'Quote',
        title: 'Send quote',
        view: 'View your quote online',
        viewAndPay: 'View and pay your quote online',
    },
    [PUBLIC_DOCUMENTS.OrderConfirmation]: {
        heading: "Here's order confirmation",
        subject: 'Order confirmation',
        title: 'Send order confirmation',
        view: 'View your order confirmation online',
        viewAndPay: 'View and pay your order confirmation online',
    },
    [PUBLIC_DOCUMENTS.TaxInvoice]: {
        heading: "Here's tax invoice",
        subject: 'Tax invoice',
        title: 'Send tax invoice',
        view: 'View your tax invoice online',
        viewAndPay: 'View and pay your tax invoice online',
    },
    [PUBLIC_DOCUMENTS.ProformaInvoice]: {
        heading: "Here's proforma invoice",
        subject: 'Proforma invoice',
        title: 'Send proforma invoice',
        view: 'View your proforma invoice online',
        viewAndPay: 'View and pay your proforma invoice online',
    },
}

// Same labels as the download menu, so the Locize keys already exist.
const DOCUMENT_LABELS = {
    [PUBLIC_DOCUMENTS.Quote]: 'Quote',
    [PUBLIC_DOCUMENTS.OrderConfirmation]: 'Order confirmation',
    [PUBLIC_DOCUMENTS.ProformaInvoice]: 'Proforma invoice',
    [PUBLIC_DOCUMENTS.TaxInvoice]: 'Tax invoice',
    [PRIVATE_DOCUMENTS.WorkOrder]: 'Work order',
    [PRIVATE_DOCUMENTS.WorkOrderSummary]: 'Work order summary',
    [PRIVATE_DOCUMENTS.DeliveryDocket]: 'Delivery docket',
    [PRIVATE_DOCUMENTS.ProductionLabels]: 'Production labels',
}

const DOCUMENT_GROUPS = [
    { isCustomerFacing: true, title: 'For the customer' },
    { isCustomerFacing: false, title: 'Internal' },
]

// No answer in time doesn't mean no email: the API may still send it after the client or
// Cloudflare (524) stops waiting, so these get a warning instead of a reopened form.
const SEND_OUTCOME_UNKNOWN = ['TIMEOUT_ERROR', 504, 524]

const EmailCustomerModal = ({
    documentToSend,
    draft,
    handleClose,
    onSendFinished,
    onSendStart,
    open,
    quote,
    quoteContact,
}) => {
    const { t } = useTranslation()
    const { closeSnackbar, enqueueSnackbar } = useSnackbar()

    const organisation = useSelector(selectOrganisation)
    const [sendInternalQuoteEmail] = useSendInternalQuoteEmailMutation()

    const { showPDFAttachmentCheckbox, showPayments } = useToolBoxTreatments()
    const { isReady: areFlagsReady, showQuoteEmailDocuments } = useFeatureFlags()
    const chooseDocuments = areFlagsReady && showQuoteEmailDocuments

    // The quote's status decides which documents exist. That rule lives in the API,
    // so the list is never derived from the status here.
    const {
        data: availableDocuments = [],
        isError: documentsFailed,
        isFetching: documentsLoading,
    } = useGetQuoteAvailableDocumentsQuery(
        { organisationId: organisation?.organisationId, quoteId: quote?.id },
        { skip: !open || !chooseDocuments || !quote?.id, refetchOnMountOrArgChange: true }
    )

    // null until the user ticks something, so the default shows without an effect. The default is the
    // document this email is about (e.g. "Send proforma invoice"), else the API's pick for the status.
    const [selectedDocuments, setSelectedDocuments] = useState(draft?.documentTypes ?? null)
    const isDocumentToSendAvailable = availableDocuments.some(({ documentType }) => documentType === documentToSend)
    const documentTypes =
        selectedDocuments ??
        (isDocumentToSendAvailable
            ? [documentToSend]
            : availableDocuments.filter(({ isDefault }) => isDefault).map(({ documentType }) => documentType))

    const toggleDocument = (documentType) =>
        setSelectedDocuments(
            documentTypes.includes(documentType)
                ? documentTypes.filter((selected) => selected !== documentType)
                : [...documentTypes, documentType]
        )

    const isInternalDocumentTicked = availableDocuments.some(
        ({ documentType, isCustomerFacing }) => !isCustomerFacing && documentTypes.includes(documentType)
    )

    const organisationOwner = organisation?.organisationUsers?.find((user) => user.organisationUserRole === 'owner')

    const paymentsEnabled = organisation?.paymentsEnabledInternal

    const quotePaymentStatus = QUOTE_PAYMENT_STATUS[quote?.paymentStatus]

    const documentCopy = DOCUMENT_EMAIL_COPY[documentToSend] ?? DOCUMENT_EMAIL_COPY[PUBLIC_DOCUMENTS.Quote]

    const documentId = useMemo(() => {
        if (documentToSend === PUBLIC_DOCUMENTS.Quote) return quote?.name
        if (documentToSend === PUBLIC_DOCUMENTS.OrderConfirmation) return quote?.name
        if (documentToSend === PUBLIC_DOCUMENTS.TaxInvoice) return quote?.invoiceNumberText
        if (documentToSend === PUBLIC_DOCUMENTS.ProformaInvoice) return quote?.name
    }, [documentToSend, quote])

    const sharedLinkURL = useMemo(
        () => `https://${window.location.host}${Paths.SHARED_PATHNAME}/v2/${quote?.id}/${documentToSend}`,
        [quote, documentToSend]
    )

    const defaultSubject = useMemo(
        () =>
            `${t(documentCopy.subject)} ${documentId} ${t('from')} ${organisation?.name} ${t('for')} ${
                quote?.customerName
            }`,
        [quote, organisation, documentCopy, documentId, t]
    )

    const formattedQuotePrice = useMemo(
        () =>
            getFormattedPrice(quote?.lineTaxedPrice, organisation.currencyCode, organisation.locale, {
                useGrouping: true,
            }),
        [quote, organisation]
    )

    const defaultMessage = useMemo(() => {
        const isPaid =
            quotePaymentStatus === QUOTE_PAYMENT_STATUS.ManualPaid ||
            quotePaymentStatus === QUOTE_PAYMENT_STATUS.GatewayPaid

        const viewLine =
            showPayments && paymentsEnabled && quotePaymentStatus === QUOTE_PAYMENT_STATUS.Unpaid
                ? t(documentCopy.viewAndPay)
                : t(documentCopy.view)

        const heading = isPaid
            ? `${t('Thank you for your payment')}. ${t(documentCopy.heading)}`
            : t(documentCopy.heading)

        return `${t('Hi')} ${quoteContact?.name}, \n
${heading} ${documentId} ${t('for')} ${formattedQuotePrice}. \n
${viewLine}: <a href="${sharedLinkURL}">${t('here')}</a> \n
${t('If you have any questions, please let us know.')} \n
${t('Regards')}, \n
${organisation?.name}`
    }, [
        t,
        quoteContact?.name,
        documentCopy,
        documentId,
        formattedQuotePrice,
        organisation?.name,
        sharedLinkURL,
        showPayments,
        quotePaymentStatus,
        paymentsEnabled,
    ])

    const defaultValues = {
        to: quoteContact?.email,
        cc: '',
        bcc: organisation?.defaultBccEmailAddress || '',
        subject: defaultSubject,
        message: defaultMessage,
        from: organisation?.name || '',
        replyTo: organisation?.webStoreSupportEmail || organisationOwner?.user?.emailAddress || '',
        includePdf: showPDFAttachmentCheckbox,
    }

    // A draft is the form of a send that failed, handed back so the user can fix and resend it.
    const { control, formState, getValues, reset } = useForm({
        mode: 'all',
        defaultValues: draft?.values ?? defaultValues,
        errors: draft?.fieldErrors,
    })

    const handleCloseDialog = () => {
        reset({
            to: quoteContact?.email,
            cc: '',
            bcc: organisation?.defaultBccEmailAddress || '',
            subject: defaultSubject,
            message: defaultMessage,
            from: organisation?.name || '',
            replyTo: organisation?.webStoreSupportEmail || organisationOwner?.user?.emailAddress || '',
        })
        setSelectedDocuments(null)
        handleClose()
    }

    const invalidAddressesMessage = (addresses) => `${t('Invalid email address')}: ${addresses.join(', ')}`

    const defaultBcc = organisation?.defaultBccEmailAddress
    const isDefaultBccPresent = (value) => Boolean(defaultBcc) && splitEmailAddresses(value).includes(defaultBcc)

    const validateAddressList = (required) => (value) => {
        const addresses = splitEmailAddresses(value)
        if (required && addresses.length === 0) return t('Required')
        const invalid = addresses.filter((address) => !validateEmail(address))
        return invalid.length === 0 || invalidAddressesMessage(invalid)
    }

    // ponytail: parses the API's text/plain 400 ("Invalid email addresses: To 'a@', Cc 'b@'");
    // switch to a structured field if the API ever returns JSON.
    const serverAddressErrors = (message) => {
        const bySlot = {}
        for (const [, slot, address] of message.matchAll(/\b(To|Cc|Bcc) '([^']*)'/g)) {
            bySlot[slot.toLowerCase()] = [...(bySlot[slot.toLowerCase()] ?? []), address]
        }
        return Object.fromEntries(
            Object.entries(bySlot).map(([slot, addresses]) => [
                slot,
                { type: 'server', message: invalidAddressesMessage(addresses) },
            ])
        )
    }

    const showError = (message) => enqueueSnackbar(message, { variant: 'error', autoHideDuration: 5000 })

    // Closes the dialog at once and sends in the background: the API renders every ticked document
    // before it answers, which can take half a minute. The dialog is unmounted by the time the send
    // settles, so the code after `await` only uses props and the snackbar.
    // A double click can land before the dialog unmounts.
    const hasSent = useRef(false)

    const handleSendEmailToCustomer = async () => {
        if (hasSent.current) return
        hasSent.current = true
        const values = getValues()
        const { bcc, cc, includePdf, message, subject, to } = values
        const sending = sendInternalQuoteEmail({
            organisationId: organisation.organisationId,
            quoteId: quote.id,
            subject,
            message,
            toEmailAddress: to,
            ccEmailAddresses: splitEmailAddresses(cc),
            bccEmailAddresses: splitEmailAddresses(bcc),
            // Never both: documentTypes replaces includePdf, which stays only for the flag-off path.
            ...(chooseDocuments ? { documentTypes } : { includePdf }),
        }).unwrap()
        onSendStart()
        handleClose()
        const sendingSnackbar = enqueueSnackbar(t('Sending email...'), { variant: 'info', persist: true })

        try {
            await sending.finally(() => closeSnackbar(sendingSnackbar))
            enqueueSnackbar(t('Email sent successfully'), { variant: 'success' })
            onSendFinished(null)
        } catch (error) {
            if (SEND_OUTCOME_UNKNOWN.includes(error?.status)) {
                enqueueSnackbar(
                    t(
                        'This is taking longer than usual. The email may still be sent, so check before sending it again.'
                    ),
                    { variant: 'warning', autoHideDuration: 10000 }
                )
                onSendFinished(null)
                return
            }

            const failedDraft = { documentToSend, documentTypes, values }
            const serverMessage = typeof error?.data === 'string' ? error.data : null
            if (error?.status === 400 && serverMessage) {
                showError(serverMessage)
                failedDraft.fieldErrors = serverAddressErrors(serverMessage)
            } else if ((error?.status === 409 || error?.status === 502) && serverMessage) {
                // 409: a ticked document is no longer allowed for the quote's status, so fall back
                // to the API's default. 502: a document failed to generate. Both messages name it.
                showError(serverMessage)
                if (error.status === 409) failedDraft.documentTypes = null
            } else if (error?.status === 429) {
                showError(t('Too many emails sent. Please wait a minute and try again.'))
            } else {
                showError(t('Failed to send email'))
            }
            onSendFinished(failedDraft)
        }
    }

    useEffect(() => {
        // keepErrors so a reopened draft keeps the server's field errors.
        reset(draft?.values ?? defaultValues, { keepErrors: Boolean(draft) })
    }, [quote, quoteContact])

    return (
        <Dialog
            maxWidth="sm"
            open={open}
            onClose={handleCloseDialog}
        >
            <DialogTitle>{t(documentCopy.title)}</DialogTitle>
            <DialogContent style={{ width: '600px' }}>
                <Box
                    style={{ gap: 20 }}
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                    }}
                >
                    <Controller
                        control={control}
                        name="to"
                        render={({ field: { onBlur, onChange, value }, fieldState }) => (
                            <TextField
                                error={Boolean(fieldState.error)}
                                helperText={fieldState.error?.message || t('Separate multiple addresses with commas')}
                                label={t('To')}
                                value={value}
                                fullWidth
                                required
                                onBlur={onBlur}
                                onChange={onChange}
                            />
                        )}
                        rules={{ validate: validateAddressList(true) }}
                    />

                    <Controller
                        control={control}
                        name="cc"
                        render={({ field: { onBlur, onChange, value }, fieldState }) => (
                            <TextField
                                error={Boolean(fieldState.error)}
                                helperText={fieldState.error?.message || t('Separate multiple addresses with commas')}
                                label={t('Cc')}
                                value={value}
                                fullWidth
                                onBlur={onBlur}
                                onChange={onChange}
                            />
                        )}
                        rules={{ validate: validateAddressList(false) }}
                    />

                    <Controller
                        control={control}
                        name="bcc"
                        render={({ field: { onBlur, onChange, value }, fieldState }) => (
                            <TextField
                                error={Boolean(fieldState.error)}
                                helperText={
                                    fieldState.error?.message ||
                                    (isDefaultBccPresent(value)
                                        ? t('Pre-filled from organisation settings')
                                        : t('Separate multiple addresses with commas'))
                                }
                                label={t('Bcc')}
                                value={value}
                                fullWidth
                                onBlur={onBlur}
                                onChange={onChange}
                            />
                        )}
                        rules={{ validate: validateAddressList(false) }}
                    />

                    <Controller
                        control={control}
                        name="from"
                        render={({ field: { onBlur, onChange, value }, fieldState }) => (
                            <TextField
                                error={Boolean(fieldState.error)}
                                helperText={fieldState.error?.message || ''}
                                label={t('From')}
                                slotProps={{
                                    input: {
                                        readOnly: true,
                                        disableUnderline: true,
                                    },
                                }}
                                value={value}
                                fullWidth
                                required
                                onBlur={onBlur}
                                onChange={onChange}
                            />
                        )}
                    />

                    <Controller
                        control={control}
                        name="replyTo"
                        render={({ field: { onBlur, onChange, value }, fieldState }) => (
                            <TextField
                                error={Boolean(fieldState.error)}
                                helperText={fieldState.error?.message || ''}
                                label={t('Reply to')}
                                slotProps={{
                                    input: {
                                        readOnly: true,
                                        disableUnderline: true,
                                    },
                                }}
                                value={value}
                                fullWidth
                                required
                                onBlur={onBlur}
                                onChange={onChange}
                            />
                        )}
                    />

                    <Controller
                        control={control}
                        name="subject"
                        render={({ field: { onBlur, onChange, value }, fieldState }) => (
                            <TextField
                                // className={classes.input}
                                error={Boolean(fieldState.error)}
                                helperText={fieldState.error?.message || ''}
                                label={t('Subject')}
                                value={value}
                                fullWidth
                                required
                                onBlur={onBlur}
                                onChange={onChange}
                            />
                        )}
                        rules={{ required: true }}
                    />
                    {chooseDocuments ? (
                        <FormControl component="fieldset">
                            {/* FormControl marks the legend focused when a checkbox takes focus,
                                which turns it orange; it is a group title, not a field label. */}
                            <FormLabel
                                component="legend"
                                focused={false}
                            >
                                {t('Attach documents')}
                            </FormLabel>
                            {documentsLoading ? <CircularProgress size={20} /> : null}
                            {documentsFailed ? (
                                <FormHelperText error>{t('Could not load the documents')}</FormHelperText>
                            ) : null}
                            <Box
                                sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', columnGap: 3 }}
                            >
                                {DOCUMENT_GROUPS.map(({ isCustomerFacing, title }) => {
                                    const groupDocuments = availableDocuments.filter(
                                        (document) => document.isCustomerFacing === isCustomerFacing
                                    )
                                    if (groupDocuments.length === 0) return null
                                    return (
                                        <FormGroup
                                            aria-label={t(title)}
                                            key={title}
                                            role="group"
                                        >
                                            <Typography
                                                color="text.secondary"
                                                variant="overline"
                                            >
                                                {t(title)}
                                            </Typography>
                                            {groupDocuments.map(({ documentType }) => (
                                                <FormControlLabel
                                                    control={
                                                        <Checkbox
                                                            checked={documentTypes.includes(documentType)}
                                                            color="primary"
                                                            onChange={() => toggleDocument(documentType)}
                                                        />
                                                    }
                                                    key={documentType}
                                                    label={t(DOCUMENT_LABELS[documentType] ?? documentType)}
                                                />
                                            ))}
                                        </FormGroup>
                                    )
                                })}
                            </Box>
                            {isInternalDocumentTicked ? (
                                <Alert severity="warning">{t('This email will include internal documents.')}</Alert>
                            ) : null}
                        </FormControl>
                    ) : null}

                    {!chooseDocuments && showPDFAttachmentCheckbox ? (
                        <FormControlLabel
                            control={
                                <Controller
                                    control={control}
                                    name="includePdf"
                                    render={({ field: { onChange, value } }) => (
                                        <Checkbox
                                            checked={Boolean(value) || false}
                                            color="primary"
                                            onChange={onChange}
                                        />
                                    )}
                                />
                            }
                            label={t('Include PDF as attachment')}
                        />
                    ) : null}

                    <Controller
                        control={control}
                        name="message"
                        render={({ field: { onBlur, onChange, value }, fieldState }) => (
                            <RichContentEditor
                                error={Boolean(fieldState.error)}
                                helperText={fieldState.error?.message || ''}
                                label={t('Message')}
                                value={value}
                                onBlur={onBlur}
                                onChange={onChange}
                            />
                        )}
                        rules={{ required: true }}
                    />
                </Box>
            </DialogContent>
            <DialogActions>
                <Button
                    color="primary"
                    disabled={
                        !formState.isValid ||
                        // Sending before the list loads would silently attach nothing.
                        (chooseDocuments && (documentsLoading || documentsFailed))
                    }
                    variant="contained"
                    onClick={handleSendEmailToCustomer}
                >
                    {t('Send')}
                </Button>
            </DialogActions>
        </Dialog>
    )
}

EmailCustomerModal.propTypes = {
    handleClose: PropTypes.func.isRequired,
    open: PropTypes.bool.isRequired,
    onSendFinished: PropTypes.func.isRequired,
    onSendStart: PropTypes.func.isRequired,
    documentToSend: PropTypes.string,
    draft: PropTypes.shape({
        documentToSend: PropTypes.string,
        documentTypes: PropTypes.arrayOf(PropTypes.string),
        fieldErrors: PropTypes.object,
        values: PropTypes.object,
    }),
    quote: PropTypes.object,
    quoteContact: PropTypes.object,
}

export default EmailCustomerModal
